import OrderAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order.entity';
import SalesOfficeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity';
import VehicleAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/transport/vehicle/vehicle.entity';

import { MarkerGroup } from 'app/common/components/geo-manager/geo-manager.interfaces';

import { apiErrorDetail } from './api-error';
import { ClientLike, clientLabel } from './client-label';
import {
  GroupOrigin,
  PlanningCoverage,
  RESIDUAL_KEY,
} from './plan-partitioning';
import { FleetUsage, PlanRoute, Reload, Unplanned } from './route-optimization.interface';
import { basePlanOrderId } from './route-optimization.mapper';
import {
  addMinutesToClock,
  outboundLegMin,
  returnLegMin,
  routeTimes,
  serviceMinBetween,
} from './route-times';

/**
 * Vista del resultado de `POST /plan`: del contrato de la API al modelo que la
 * pantalla pinta.
 *
 * Por qué existe como módulo aparte: la traducción `PlanResponse` → tarjetas de
 * camión, paradas, no-asignadas y KPIs no depende de QUÉ pantalla lanzó la
 * corrida. Vivía incrustada en "Rutas AI"; al necesitarla también la sugerencia
 * de Asignación de Carga, la alternativa era copiarla — y dos copias de la misma
 * regla de negocio (el peso por parada, la capacidad medida contra el viaje más
 * cargado, la fusión de no-asignadas por motivo) divergen a la primera
 * corrección.
 *
 * Módulo puro: entran respuestas del motor y órdenes, sale el modelo de vista.
 * Sin HTTP, sin estado de componente, sin Angular.
 */

/** Paleta estable por camión (consistente lista ↔ mapa). */
export const TRUCK_COLORS: readonly string[] = [
  '#6366f1', // índigo
  '#14b8a6', // teal
  '#8b5cf6', // violeta
  '#f59e0b', // ámbar
  '#ec4899', // rosa
  '#0ea5e9', // celeste
  '#22c55e', // verde
];

/** Color del marcador del almacén (oficina de ventas) en el mapa. */
export const WAREHOUSE_COLOR = '#0f172a'; // slate-900: destaca frente a la paleta

export const LB_TO_KG = 0.453592;
export const KG_TO_LB = 2.20462;

/** Capacidad de respaldo cuando el vehículo no declara una usable. */
export const DEFAULT_CAPACITY_LB = 10_000;

/** Color de camión por índice, ciclando la paleta. */
export function truckColorAt(index: number): string {
  return TRUCK_COLORS[index % TRUCK_COLORS.length];
}

/**
 * Motivos de no-asignación del motor, en el idioma del operador.
 *
 * Deliberadamente **cortos**: se usan como encabezado del grupo y también,
 * en minúscula, dentro del resumen de una línea (`unassignedSummary`). Lo que
 * hay que hacer al respecto vive aparte, en `REASON_HINTS`.
 */
export const REASON_LABELS: Readonly<Record<string, string>> = {
  invalid_location: 'Coordenada inutilizable',
  swapped_location: 'Coordenada invertida',
  exceeds_capacity: 'Más pesada que el camión más grande',
  out_of_range: 'Fuera de alcance',
  shift_full: 'Sin cupo en la jornada',
  // Habla de la FLOTA y del DÍA, no de este pedido: el servicio lo emite cuando la
  // demanda total del grupo supera lo que la flota puede cargar (`total_demand >
  // fleet_capacity`). Decía "Sin capacidad en el camión" y el consejo hablaba del peso
  // del pedido, así que una parada de 0 lb aparecía acusada de pesar demasiado.
  no_capacity: 'La flota no alcanza para todo el grupo',
  window: 'Ventana horaria incompatible',
  unreachable: 'Dirección no alcanzable',
  no_eligible_day: 'Sin día elegible',
  due_date: 'Fuera de fecha de entrega',
  exceeds_max_days: 'No entra en los días del tour',
  other: 'No se pudo asignar',
};

/**
 * Qué mirar para resolver cada motivo.
 *
 * Separado de la etiqueta porque responde otra pregunta: `REASON_LABELS` dice
 * QUÉ pasó, esto dice QUÉ HACER. Un motivo sin acción concreta —`other`— no
 * tiene entrada: inventarle un consejo genérico sería ruido con forma de ayuda.
 */
export const REASON_HINTS: Readonly<Record<string, string>> = {
  invalid_location:
    'Está tan lejos del almacén que no puede ser real: revisar la geolocalización del cliente.',
  swapped_location:
    'Latitud y longitud parecen intercambiadas: al invertirlas el punto cae en la zona de reparto.',
  exceeds_capacity:
    'Sola supera la capacidad del mayor camión y no se hace entrega parcial: ' +
    'reenviarla dividida en pedidos que quepan, o asignarle un camión más grande.',
  out_of_range: 'No cabe en un día desde la base, ni yendo sola.',
  shift_full: 'Es servible: agregar un camión o pasarla a otro día.',
  no_capacity:
    'No es por este pedido: la suma del grupo supera lo que la flota carga en el día ' +
    '(contando recargas). Agregar camión o repartir el grupo en más días.',
  window: 'La ventana horaria del cliente no entra en la jornada.',
  unreachable: 'Revisar las coordenadas del cliente: no se puede rutear hasta ahí.',
  no_eligible_day: 'Ningún día del rango planificado le sirve.',
  due_date: 'Su fecha de entrega cae fuera del rango planificado.',
  exceeds_max_days: 'El recorrido de larga distancia no alcanza para llegar.',
};

/** Punto de entrega (parada / no asignada) con coordenadas y peso. */
export interface DeliveryPoint {
  /**
   * Id de la orden que representa el punto.
   *
   * Lo llevan también los no asignados y los excluidos, no solo las paradas: es lo
   * único que hace falta para resolver sus pedidos con `stopOrders`, y sin él el
   * detalle de materiales solo existía para lo que SÍ se entrega — justo la mitad
   * menos interesante cuando se está averiguando por qué algo no entró.
   */
  orderId: string;
  /**
   * Llave de la entrega (grupo de planificación) a la que pertenece el pedido.
   *
   * Se deriva de la **orden**, no del grupo que la contuvo, y por eso también la
   * llevan los excluidos —los que nunca llegaron al motor y por lo tanto a ningún
   * grupo—. Sin eso, filtrar por entrega los mostraba todos o los escondía todos:
   * las dos respuestas mienten sobre la única lista donde uno va a averiguar por
   * qué un pedido no se entrega.
   */
  routeKey: string;
  /**
   * Cómo se identifica el cliente: `CÓDIGO — NOMBRE LEGAL` (ver `clientLabel`).
   *
   * El código es lo que cruza con el maestro y con el ERP. Antes acá venía el nombre
   * COMERCIAL y nada más, así que el mismo cliente se leía distinto en esta pantalla
   * y en `master/routing/sub-route`, y no había forma de cruzarlo sin buscar por
   * nombre — que ni es único ni coincidía.
   */
  name: string;
  /**
   * Nombre comercial, cuando dice algo distinto del legal. Va como dato adicional
   * (un tag) y no dentro de `name`: la fila de parada trunca, y un tag tiene ancho
   * propio que sobrevive al truncado.
   *
   * Opcional también por la caché de corrida: una foto anterior rehidrata sin él.
   */
  commercialName?: string;
  /**
   * Serial del documento: el número que el operador cruza con el maestro.
   *
   * Es lo único que distingue dos pedidos del MISMO cliente — mismo nombre, misma
   * dirección y, desde que el peso es el del pedido y no el del cliente, pesos que
   * también pueden coincidir. Sin él, dos filas legítimas se leen como una repetida.
   *
   * Vive en el documento, no en la parada, así que se resuelve acá donde la orden ya
   * está: el popup del mapa lo armaba por su cuenta, y esa era una segunda copia de
   * la misma resolución.
   */
  serial?: string;
  address: string;
  /** Territorio(s) de la orden (acotado a los seleccionados si hay filtro). */
  territory: string;
  /**
   * Ruta que sirve la parada, para rotularla junto al territorio.
   *
   * Opcional y aditivo: solo lo llena la pantalla que rotula ambos conceptos.
   * Ruta y territorio suelen compartir número y distinguirse por el prefijo
   * (`ENTREGA 809` / `TEGUCIGALPA 809`), así que un valor suelto es
   * indistinguible del otro.
   */
  routeName?: string;
  /**
   * `true` si el territorio de la orden **no** está entre los que declara su
   * ruta. Es un dato del maestro a corregir, y se muestra en vez de ocultarse.
   */
  territoryMismatch?: boolean;
  lat: number | null;
  lon: number | null;
  weightLb: number;
  weightKg: number;
}

/** Parada de un camión para la lista. */
export interface StopRow extends DeliveryPoint {
  sequence: number;
  arrivalTime: string;
  /** Hora de salida = llegada + atención. La API ya la devolvía sin usarse. */
  departureTime: string;
  /** Minutos de atención en la parada, derivados de `salida − llegada`. */
  serviceMin: number;
  /**
   * Minutos de viaje desde la parada anterior. En la **primera** trae el viaje desde
   * el almacén, no `0`: el servicio lo calcula como `step.duration - prev_duration`
   * con `prev_duration` en cero y salteando el paso `start` de VROOM, así que la
   * primera parada resta contra el origen. Este comentario decía lo contrario y
   * llevó a contar la ida dos veces al derivar el regreso.
   */
  legDurationMin: number;
  /** Minutos desde el inicio del turno (incluye la atención acumulada). */
  etaMin: number;
  /** Viaje al que pertenece la parada (1 en modo un-viaje). */
  trip: number;
}

/** Ruta de un camión (un viaje propuesto). */
export interface TruckRouteView {
  truckId: string;
  /**
   * Llave del grupo de planificación que produjo esta ruta.
   *
   * Es lo que empareja la tarjeta con su tag de entrega. No sirve `subRouteName`:
   * cuando la salvaguarda de tamaño parte un grupo por municipio, la etiqueta se
   * vuelve `ENTREGA 301 · COMAYAGUA` y ya no coincide con nada.
   */
  groupKey: string;
  label: string;
  color: string;
  /**
   * `_id` de Mongo de la sub-ruta que originó el grupo, si salió de una
   * (ausente en grupos por municipio o residuales). Necesario para poder
   * confirmar esta ruta contra `POST trip/confirm-optimized-routes` — el
   * `truck_id` que devuelve el motor no sirve para esto (es el id del
   * vehículo o un fallback interno, no la sub-ruta).
   */
  subRouteId?: string;
  /** Nombre de la RUTA de este camión (la sub-ruta que lo declara en su territorio). */
  subRouteName: string;
  /** Territorio de la ruta, para mostrarlo junto al nombre y no en su lugar. */
  territoryName?: string;
  driver: string;
  vehicleLabel: string;
  weightLb: number;
  weightKg: number;
  capacityLb: number;
  capacityKg: number;
  capacityPct: number;
  distanceKm: number;
  /** Tiempo de VIAJE de la ruta (lo que devuelve el motor). */
  durationMin: number;
  /** Atención total: la suma de la de sus paradas. */
  serviceMin: number;
  /** Jornada: viaje + atención. Es lo que la operación realmente ocupa. */
  workMin: number;
  /**
   * Minutos del almacén a la PRIMERA parada, ya contados en `durationMin`.
   *
   * Se expone porque la lista arrancaba en la parada 1 y ese tramo —que en un
   * territorio lejano son dos horas y media— no tenía fila propia: se leía "llega
   * 10:33" sin decir de dónde venía ni cuándo salió el camión.
   */
  outboundMin: number;
  /** Hora de salida del almacén, o `''` si no se puede derivar. */
  departureTime: string;
  /**
   * Minutos del regreso al almacén, ya contados en `durationMin`.
   *
   * Se expone aparte porque no se veía en ninguna parte: el camión sale del almacén y
   * vuelve a él (`start`/`end` del vehículo en VROOM), así que el viaje de vuelta está
   * dentro del total, pero la última fila era la última parada y la jornada parecía no
   * cuadrar. En una ruta a un territorio lejano son horas, no minutos.
   */
  returnMin: number;
  /** Hora de llegada de vuelta al almacén, o `''` si no se puede derivar. */
  returnArrivalTime: string;
  /**
   * Si la tarjeta muestra sus paradas. Arranca **cerrada**.
   *
   * No es solo orden visual: las paradas se renderizan con `*ngIf`, así que cerradas
   * NO existen en el DOM. Una corrida real son 17 rutas × ~14 paradas = 246 filas, y
   * cada una lleva nombre, dirección, dos chips, pesos y cuatro tiempos — unos 2.500
   * nodos que además se reevalúan en cada ciclo de detección de cambios, que es lo que
   * volvía lento el modal al escribir en el buscador.
   *
   * Con CSS (`hidden` o una clase) el DOM se construye igual y la lentitud queda: el
   * `*ngIf` es la parte que importa.
   */
  open: boolean;
  stops: StopRow[];
  /** Nº de viajes del camión (1 = sin recargas). */
  trips: number;
  /** Regresos al almacén entre viajes. */
  reloads: Reload[];
  /** Peso del viaje más cargado (la capacidad se resetea por viaje). */
  heaviestTripLb: number;
  heaviestTripKg: number;
}

/** No asignadas agrupadas por motivo. */
export interface UnassignedGroup {
  reason: string;
  label: string;
  /** Qué hacer al respecto; ausente en los motivos sin acción concreta. */
  hint?: string;
  items: DeliveryPoint[];
}

/** KPIs de un plan (por oficina o agregados). */
export interface Dash {
  trucks: number;
  distanceKm: number;
  /** Tiempo de VIAJE agregado (lo que devuelve el motor). */
  durationMin: number;
  /** Atención agregada a clientes. */
  serviceMin: number;
  /** Jornada agregada: viaje + atención. */
  workMin: number;
  assigned: number;
  total: number;
}

/** Estado de la planificación de un grupo dentro de la corrida. */
export type GroupStatus = 'pending' | 'running' | 'done' | 'failed';

/** Un subproblema de planificación (una sub-ruta, en general) y su estado. */
export interface GroupPlanView {
  key: string;
  /**
   * Nombre a mostrar del grupo: **la RUTA**, nunca el territorio.
   *
   * Con un territorio servido por VARIAS rutas cae al nombre del territorio, que es
   * lo único cierto: el grupo agrupa las paradas del territorio, y nombrarlo con una
   * de sus rutas afirmaría que abarca menos de lo que abarca.
   */
  label: string;
  /** Cómo se formó el grupo (sub-ruta / municipio / residual). */
  origin: GroupOrigin;
  /**
   * Sub-ruta que originó el grupo, si salió de una. Permite recuperar su
   * maestro —y con él los territorios que declara— sin re-derivarlo de `key`.
   */
  subRouteId?: string;
  /** Paradas enviadas al motor (post agrupación por cliente). */
  stops: number;
  status: GroupStatus;
  error?: string;
  /** Territorio del grupo. La unidad de planificación es el territorio. */
  territoryId?: number;
  /**
   * Nombre del territorio del grupo.
   *
   * Existe como campo propio porque `label` pasó a significar SIEMPRE la ruta: al
   * cambiar la unidad de planificación de sub-ruta a territorio, `label` se llenó
   * con el nombre del territorio y ninguna superficie lo notó —ni un test ni el
   * compilador ven que un `string` cambió de concepto—, así que las tarjetas, los
   * tags y el selector del mapa quedaron nombrando territorios donde el operador
   * espera rutas. Un campo por concepto elimina esa clase de rotación silenciosa.
   */
  territoryName?: string;
  /**
   * `true` si el camión y el turno salieron del maestro de sub-ruta; `false` si
   * se usó la configuración de respaldo de la pantalla. Sin esto, un plan hecho
   * con la capacidad por defecto es indistinguible de uno con el camión real.
   *
   * Ya no puede ser `false` por falta de CAMIÓN —sin camión no hay grupo—: hoy
   * significa que el camión existe pero su capacidad o su jornada no están
   * declaradas en el maestro.
   */
  fleetFromMaster: boolean;
  /** Cuántos camiones reales viajaron en la petición de este territorio. */
  truckCount?: number;
  /** La sub-ruta apunta a un vehículo que el usuario excluyó del pool. */
  vehicleUnavailable: boolean;
  /**
   * Color del grupo, asignado al **crearlo** y no al recibir su respuesta.
   *
   * Las corridas son concurrentes: derivarlo del orden de llegada hacía que dos
   * corridas idénticas pintaran colores distintos, y hace imposible mandarle el
   * color al motor en el request (que se arma antes de la respuesta).
   */
  color?: string;
  /** Avisos del grupo (p. ej. recargas que no caben en su franja). */
  warnings: string[];
}

/** Resultado de la planificación de UNA oficina de ventas. */
export interface OfficePlanView {
  office: SalesOfficeAttributes;
  officeId: number;
  ordersCount: number;
  truckViews: TruckRouteView[];
  unassignedGroups: UnassignedGroup[];
  dash?: Dash;
  /** Uso por tipo de camión (solo camino por oficina en modo flota mixta). */
  fleetUsage: FleetUsage[];
  /** Error de la carga de órdenes de esta oficina (no aborta a las demás). */
  error?: string;
  /**
   * Aviso de que se planificó con **parte** de los pedidos.
   *
   * Distinto de `error`: ahí no hay plan, acá hay uno hecho sobre un subconjunto.
   * Se declara porque un plan silencioso sobre datos incompletos se lee como
   * completo, y es la clase de cosa con la que se despachan camiones de menos.
   */
  ordersWarning?: string;
  /** Colapsable de sin-asignar de esta oficina. */
  unassignedOpen: boolean;
  /** Colapsable del estado por ruta (cerrado por defecto, como los otros dos). */
  groupsOpen: boolean;
  /** Cobertura de los datos que la planificación necesita. */
  coverage?: PlanningCoverage;
  /** Órdenes que no llegaron al motor por coordenadas inválidas. */
  excludedPoints: DeliveryPoint[];
  /** Colapsable del detalle de excluidas. */
  excludedOpen: boolean;
  /**
   * Pedidos SIN ruta asignada en el maestro. A diferencia de los excluidos, SÍ
   * se planifican —agrupados por municipio— pero su detalle quedaba enterrado
   * dentro de la tarjeta de ese grupo: esta lista les da la misma entrada
   * directa que tienen los excluidos.
   */
  withoutRoutePoints: DeliveryPoint[];
  /** Colapsable del detalle de los sin ruta. */
  withoutRouteOpen: boolean;
  /**
   * Una línea por pedido auto-asignado a una ruta (por territorio o cercanía),
   * para el tooltip del chip: automatizar sí, en silencio no.
   */
  autoAssignedNotes: string[];
  /** Subproblemas de esta oficina (vacío en el camino por oficina). */
  groups: GroupPlanView[];
  /**
   * Territorios con pedidos y **sin camión asignado**: la brecha de flota.
   *
   * Es el reemplazo del KPI de "rutas con flota de respaldo". Esa condición
   * dejó de existir —un territorio sin camión ya no se planifica con una
   * capacidad inventada— y el dato accionable pasó a ser cuál territorio no
   * tiene con qué salir, porque se resuelve asignando un camión en el maestro.
   */
  fleetGaps: FleetGap[];
  /** Colapsable del detalle de la brecha de flota. */
  fleetGapOpen: boolean;
  /**
   * Avisos de flota de la corrida: el camión asignado a territorios distintos,
   * que se excluye para no sobrevenderlo en dos peticiones.
   */
  fleetWarnings: string[];
}

/** Un territorio con pedidos que ningún camión asignado puede llevar. */
export interface FleetGap {
  territoryId?: number;
  territoryName: string;
  /**
   * Ruta de entrega del territorio, cuando existe.
   *
   * Si el mensaje dice que se arregla en el maestro de rutas, hay que saber cuál
   * abrir: nombrar solo el territorio devuelve al operador un paso de traducción
   * que el optimizador ya resolvió. Ausente cuando el territorio no tiene ruta —
   * que es justamente una de las causas.
   */
  routeName?: string;
  /** Motivo, ya en el vocabulario de la pantalla. */
  reason: string;
  /** Qué hacer y dónde. Va junto al motivo, no en un tooltip. */
  action: string;
  /** Clave de la causa, para agrupar los renglones que la comparten. */
  causeKey: string;
  /**
   * Clientes DISTINTOS del renglón.
   *
   * Es el número que dice cuántas fichas hay que tocar, y es otro trabajo que el
   * de repartir: un cliente con cinco pedidos es una corrección, no cinco.
   */
  clients: number;
  orders: number;
  weightLb: number;
  /** Pedidos del territorio, para la misma entrada directa que los excluidos. */
  points: DeliveryPoint[];
  /**
   * Dimensionamiento: cuántos camiones y de qué capacidad harían falta. Ausente
   * mientras no se pidió, o si su petición falló (cobertura parcial declarada).
   */
  sizing?: FleetSizing;
}

/** Cuánta flota haría falta en un territorio, según el modo `fleet` de `/plan`. */
export interface FleetSizing {
  /** Total de camiones necesarios. */
  trucks: number;
  /** Mezcla recomendada: cuántos de cada capacidad, en la unidad de la pantalla. */
  mix: Array<{ capacityLb: number; used: number }>;
  /**
   * `true` si el dimensionamiento dejó pedidos sin asignar: la cifra es un
   * MÍNIMO, no la respuesta. Presentarla como exacta sería peor que no darla.
   */
  isMinimum: boolean;
  /** Pedidos que no entraron ni con el techo de unidades. */
  unassigned: number;
  /**
   * `true` si la capacidad del molde salió de la pantalla y no de la flota real
   * de la oficina (porque no tiene ningún camión asignado): es un supuesto.
   */
  capacityAssumed: boolean;
}

/** Sección vacía de una oficina, lista para llenarse grupo a grupo. */
export function createOfficePlanView(office: SalesOfficeAttributes): OfficePlanView {
  return {
    office,
    officeId: office.id as number,
    ordersCount: 0,
    truckViews: [],
    unassignedGroups: [],
    fleetUsage: [],
    unassignedOpen: false,
    groupsOpen: false,
    excludedPoints: [],
    excludedOpen: false,
    withoutRoutePoints: [],
    withoutRouteOpen: false,
    autoAssignedNotes: [],
    groups: [],
    fleetGaps: [],
    fleetGapOpen: false,
    fleetWarnings: [],
  };
}

/**
 * Geo de la oficina como `[lat, lon]` válido, o `null`.
 *
 * Un `0` exacto en cualquier componente es un registro sin geocodificar (Null
 * Island), no una coordenada: se descarta igual que en el mapper de órdenes.
 */
export function officeGeoOf(office: SalesOfficeAttributes): [number, number] | null {
  const geo = office?.address?.geolocationPoint;
  if (!geo || geo.length < 2) return null;
  const [lat, lon] = geo;
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat === 0 || lon === 0) return null;
  return [lat, lon];
}

/**
 * Capacidad del vehículo NORMALIZADA a libras: `mst-vehicle` mezcla unidades
 * (hay `grossWeight` declarados en KG y en LB) — mismo criterio que el peso de
 * las órdenes, que es contra lo que se compara.
 */
/**
 * Capacidad del vehículo en libras, **rellenando con el defecto** cuando el maestro
 * no la declara.
 *
 * El nombre dice que rellena porque el punto de uso tenía que poder verlo: existe
 * otra `capacityLbOf` —en `subroute-fleet`— que devuelve `undefined` en ese caso y
 * deja decidir al caller, y las dos se importan en el mismo diálogo. Dos nombres
 * iguales con contratos distintos se eligen mal, y el que rellena en silencio es
 * justo el que no conviene elegir sin querer.
 */
export function capacityLbOrDefault(vehicle?: VehicleAttributes): number {
  const raw = vehicle?.dimensions?.grossWeight ?? 0;
  if (raw <= 0) return DEFAULT_CAPACITY_LB;
  const unit = vehicle?.dimensions?.weightUnitOfMeasure?.code?.toUpperCase();
  return unit === 'KG' ? raw * KG_TO_LB : raw;
}

/** Etiqueta del vehículo para la tarjeta ("Nombre · Placa"). */
export function vehicleLabelOf(vehicle?: VehicleAttributes): string {
  if (!vehicle) return 'Vehículo por asignar';
  return [vehicle.name ?? vehicle.code, vehicle.plate].filter(Boolean).join(' · ');
}

/** Datos que hacen falta para describir una parada más allá de la respuesta del motor. */
export interface PointContext {
  /**
   * Territorio (clave de grupo) de cada pedido, según la partición.
   *
   * Es la **única** fuente: con partición por territorio la clave depende de qué
   * territorios tienen flota, así que no se puede derivar de la orden sola. Sin
   * entrada acá, el punto cae al residual — y eso es correcto para un pedido que
   * no se planificó, en vez de darle una clave que no empareja con ningún grupo.
   */
  groupKeyByOrderId?: ReadonlyMap<string, string>;
  /** Peso (lb) por `PlanOrder.id` del request (incluye paradas divididas). */
  weightByOrderId: ReadonlyMap<string, number>;
  /** Peso (lb) acumulado por cliente, como respaldo. */
  weightByCustomer: ReadonlyMap<string, number>;
  /**
   * Territorio(s) de la orden para la lista.
   *
   * Puede devolver solo el texto —lo que hacen las pantallas que muestran un
   * territorio suelto— o el detalle con la ruta y la discrepancia, para las que
   * rotulan ambos conceptos. La unión evita duplicar el cálculo en dos
   * funciones que después divergirían.
   */
  territoryLabel: (order?: OrderAttributes) => string | TerritoryParts;
}

/** Territorio de una parada con su ruta y si se contradicen. */
export interface TerritoryParts {
  territory: string;
  routeName?: string;
  territoryMismatch?: boolean;
}

/** `_id` de una orden como string. */
export function orderIdOf(order: OrderAttributes): string {
  return (order as { _id?: { toString(): string } })?._id?.toString() ?? '';
}

/** `shipTo._id` (o `shipTo.id`) de una orden como string. */
export function customerIdOf(order?: OrderAttributes): string {
  const shipTo = order?.shipTo as { _id?: { toString(): string }; id?: unknown } | undefined;
  return String(shipTo?._id ?? shipTo?.id ?? '');
}

/**
 * Fábrica del `DeliveryPoint` de una parada a partir de las órdenes cargadas.
 *
 * El motor solo devuelve `order_id`: nombre, dirección, territorio, coordenadas
 * y peso salen de la orden original. Los ids de paradas **divididas** (`__sN`,
 * cuando un cliente excede la capacidad de un camión) se resuelven a su orden
 * base, porque esa parada extra no existe como documento.
 */
export function makePointOf(
  orders: OrderAttributes[],
  ctx: PointContext,
): (id: string) => DeliveryPoint {
  const orderById = new Map(orders.map((o) => [orderIdOf(o), o]));
  return (id: string): DeliveryPoint => {
    const order = orderById.get(basePlanOrderId(id));
    const geo = order?.address?.geolocationPoint;
    // Peso de ESTA parada (incluye división por capacidad); si no, el del cliente.
    const weightLb =
      ctx.weightByOrderId.get(id) ?? ctx.weightByCustomer.get(customerIdOf(order)) ?? 0;
    const label = ctx.territoryLabel(order);
    const territory: TerritoryParts = typeof label === 'string' ? { territory: label } : label;
    return {
      // El id del PLAN, con su sufijo `__sN` si la visita se dividió: `stopOrders`
      // lo necesita tal cual para saber que el detalle es del cliente completo.
      orderId: id,
      // El mapa de la partición es la ÚNICA fuente: con partición por territorio
      // la clave depende de qué territorios tienen flota, así que no se puede
      // derivar de la orden sola. Sin entrada en el mapa no hay entrega que
      // afirmar —un pedido sin flota, o uno cuya orden no se resolvió— y cae al
      // residual en vez de inventarle una clave que no empareja con ningún grupo.
      routeKey: (order && ctx.groupKeyByOrderId?.get(orderIdOf(order))) ?? RESIDUAL_KEY,
      ...clientLabel(order?.shipTo as ClientLike | undefined, basePlanOrderId(id)),
      serial: (order as unknown as { serial?: string } | undefined)?.serial || undefined,
      address: order?.address?.address ?? '',
      ...territory,
      lat: geo && geo.length >= 2 ? geo[0] : null,
      lon: geo && geo.length >= 2 ? geo[1] : null,
      weightLb,
      weightKg: weightLb * LB_TO_KG,
    };
  };
}

/** Identidad visual y flota de un camión del resultado. */
export interface TruckViewMeta {
  /** Llave del grupo que produjo la ruta; ver `TruckRouteView.groupKey`. */
  groupKey: string;
  label: string;
  color: string;
  subRouteId?: string;
  subRouteName: string;
  territoryName?: string;
  capacityLb: number;
  vehicle?: VehicleAttributes;
  driver?: string;
}

/**
 * Traduce una ruta del motor a la tarjeta de camión.
 *
 * La barra de capacidad mide el **viaje más cargado**, no el total del día: con
 * recargas el camión descarga y vuelve a llenarse, así que sumar los viajes daría
 * un porcentaje que no significa nada. El total del día se conserva aparte.
 */
export function buildTruckRouteView(
  route: PlanRoute,
  meta: TruckViewMeta,
  pointOf: (id: string) => DeliveryPoint,
): TruckRouteView {
  const stops: StopRow[] = (route.stops ?? []).map((s) => ({
    sequence: s.sequence,
    arrivalTime: s.arrival_time,
    departureTime: s.departure_time,
    // La atención no viaja como número: se deriva de las dos horas de reloj,
    // porque el motor escribe `departure = arrival + service_s`.
    serviceMin: serviceMinBetween(s.arrival_time, s.departure_time),
    legDurationMin: s.leg_duration_min ?? 0,
    etaMin: s.eta_min ?? 0,
    trip: s.trip ?? 1,
    ...pointOf(s.order_id),
  }));

  const tripWeights = new Map<number, number>();
  for (const s of stops) tripWeights.set(s.trip, (tripWeights.get(s.trip) ?? 0) + s.weightLb);
  const heaviestTripLb = Math.max(0, ...tripWeights.values());

  const capacityLb = meta.capacityLb;
  const weightKg = route.total_weight_kg ?? 0;
  const times = routeTimes(stops, route.total_duration_min ?? 0);
  // El regreso al almacén está dentro del viaje total, pero no lo informa ninguna
  // parada: se aísla por diferencia para poder mostrarlo (ver `returnLegMin`).
  const returnMin = returnLegMin(stops, times.travelMin);
  const outboundMin = outboundLegMin(stops);
  const lastDeparture = stops.length ? stops[stops.length - 1].departureTime : undefined;
  // La salida del almacén se deriva hacia atrás desde la llegada de la primera parada:
  // el contrato no la trae, y el turno configurado no sirve porque el motor puede
  // arrancar más tarde si le conviene.
  const firstArrival = stops.length ? stops[0].arrivalTime : undefined;

  return {
    truckId: route.truck_id,
    groupKey: meta.groupKey,
    label: meta.label,
    color: meta.color,
    subRouteId: meta.subRouteId,
    subRouteName: meta.subRouteName,
    territoryName: meta.territoryName,
    driver: meta.driver ?? 'Sin asignar',
    vehicleLabel: vehicleLabelOf(meta.vehicle),
    weightLb: weightKg / LB_TO_KG,
    weightKg,
    capacityLb,
    capacityKg: capacityLb * LB_TO_KG,
    capacityPct: capacityLb > 0 ? Math.min(100, (heaviestTripLb / capacityLb) * 100) : 0,
    distanceKm: (route.stops ?? []).reduce((n, s) => n + (s.leg_distance_km ?? 0), 0),
    // `total_duration_min` es tiempo de VIAJE; la jornada se compone acá.
    durationMin: times.travelMin,
    serviceMin: times.serviceMin,
    workMin: times.workMin,
    outboundMin,
    departureTime: outboundMin > 0 ? addMinutesToClock(firstArrival, -outboundMin) : '',
    returnMin,
    returnArrivalTime: returnMin > 0 ? addMinutesToClock(lastDeparture, returnMin) : '',
    // Cerrada: son 17 tarjetas y 246 paradas en la corrida real.
    open: false,
    stops,
    trips: Math.max(1, ...stops.map((s) => s.trip)),
    reloads: route.reloads ?? [],
    heaviestTripLb,
    heaviestTripKg: heaviestTripLb * LB_TO_KG,
  };
}

/**
 * Funde las no-asignadas nuevas con las que ya había, agrupadas por motivo.
 *
 * Se acumulan porque en el camino por sub-ruta cada grupo responde por separado:
 * al usuario le importa "qué no entró y por qué", no de qué petición salió.
 */
export function mergeUnassigned(
  existing: UnassignedGroup[],
  unassigned: Unplanned[] | undefined,
  pointOf: (id: string) => DeliveryPoint,
): UnassignedGroup[] {
  const byReason = new Map<string, DeliveryPoint[]>(existing.map((g) => [g.reason, [...g.items]]));
  for (const u of unassigned ?? []) {
    const list = byReason.get(u.reason) ?? [];
    list.push(pointOf(u.order_id));
    byReason.set(u.reason, list);
  }
  return [...byReason.entries()].map(([reason, items]) => ({
    reason,
    label: REASON_LABELS[reason] ?? 'No se pudo asignar',
    hint: REASON_HINTS[reason],
    items,
  }));
}

/**
 * Una fila del resumen por oficina: la cadena completa de conciliación.
 *
 * Existe porque los KPIs agregados cambian de unidad **en silencio**: arriba se
 * anuncian 348 PEDIDOS cargados y la tarjeta de ubicados dice 301/327 — en
 * VISITAS. Los 21 que "faltan" son exclusiones (sin coordenadas, sin identidad)
 * más el agrupamiento de pedidos de un mismo cliente en una visita, y cada
 * sección de oficina explica su propia conversión, pero el agregado no: el
 * usuario ve un agujero donde hay dos cambios de unidad.
 */
export interface OfficeSummaryRow {
  name: string;
  /** Pedidos cargados de la oficina. */
  loaded: number;
  /** Pedidos que no llegaron al motor (sin coordenadas o sin identidad). */
  excluded: number;
  /** Visitas enviadas al motor (pedidos de un mismo cliente = una visita). */
  visits: number;
  /** Visitas ubicadas en alguna ruta. */
  placed: number;
  /** Visitas que quedaron sin ubicar. */
  unplaced: number;
  routes: number;
  distanceKm: number;
  workMin: number;
  /** Error de carga de la oficina, si lo hubo: la fila queda en cero y lo dice. */
  error?: string;
}

/** El resumen por oficina, con su fila de totales. */
export interface OfficeSummary {
  rows: OfficeSummaryRow[];
  total: OfficeSummaryRow;
}

/** La fila de UNA oficina; ver `OfficeSummaryRow`. */
function officeSummaryRowOf(view: OfficePlanView): OfficeSummaryRow {
  const loaded = view.coverage?.loaded ?? view.ordersCount;
  const plannable = view.coverage?.plannable ?? loaded;
  return {
    name: view.office?.name ?? String(view.officeId),
    loaded,
    excluded: Math.max(0, loaded - plannable),
    visits: view.dash?.total ?? 0,
    placed: view.dash?.assigned ?? 0,
    unplaced: Math.max(0, (view.dash?.total ?? 0) - (view.dash?.assigned ?? 0)),
    routes: view.dash?.trucks ?? 0,
    distanceKm: view.dash?.distanceKm ?? 0,
    workMin: view.dash?.workMin ?? 0,
    error: view.error,
  };
}

/**
 * Resumen por oficina + totales, listo para la tabla.
 *
 * Los totales se suman de las filas y no de `aggregateDash` a propósito: así la
 * tabla cierra contra sí misma por construcción, que es exactamente lo que el
 * usuario está tratando de verificar cuando la mira.
 */
export function buildOfficeSummary(views: readonly OfficePlanView[]): OfficeSummary {
  const rows = (views ?? []).map(officeSummaryRowOf);
  const total = rows.reduce<OfficeSummaryRow>(
    (acc, r) => ({
      name: acc.name,
      loaded: acc.loaded + r.loaded,
      excluded: acc.excluded + r.excluded,
      visits: acc.visits + r.visits,
      placed: acc.placed + r.placed,
      unplaced: acc.unplaced + r.unplaced,
      routes: acc.routes + r.routes,
      distanceKm: acc.distanceKm + r.distanceKm,
      workMin: acc.workMin + r.workMin,
    }),
    {
      name: 'Total',
      loaded: 0,
      excluded: 0,
      visits: 0,
      placed: 0,
      unplaced: 0,
      routes: 0,
      distanceKm: 0,
      workMin: 0,
    },
  );
  return { rows, total };
}

/**
 * Desglose de una línea de por qué quedaron pedidos afuera, para leerlo **sin
 * desplegar** la sección: `"12 sin cupo en la jornada · 6 fuera de alcance"`.
 *
 * Ordenado de mayor a menor porque el motivo que más pedidos afecta es el que
 * decide qué hacer con la corrida; el que afecta a uno es una anécdota.
 *
 * Los grupos vacíos se omiten: `mergeUnassigned` puede dejarlos al acumular
 * entre oficinas, y un "0 sin capacidad" en el resumen es ruido.
 */
export function unassignedSummary(
  groups: readonly UnassignedGroup[],
  opts?: { counts?: boolean },
): string {
  // `counts: false` nombra los motivos SIN cifras. Existe porque el resumen también se
  // usa como subtítulo de un descuento que ya trae su propia cantidad, y esa está en
  // PEDIDOS mientras estas están por visita: dos cifras contiguas en unidades distintas
  // es el defecto que la cascada de cobertura vino a eliminar. Cuántos hay por motivo
  // sigue estando dentro del panel que ese descuento abre.
  const counts = opts?.counts !== false;
  return [...(groups ?? [])]
    .filter((g) => g.items.length > 0)
    .sort((a, b) => b.items.length - a.items.length)
    .map((g) => (counts ? `${g.items.length} ${g.label.toLocaleLowerCase()}` : g.label.toLocaleLowerCase()))
    .join(' · ');
}

/**
 * Resumen del estado por ruta, para leerlo **sin desplegar** el detalle.
 *
 * Ya no cuenta "rutas con camión y jornada por defecto": esa condición dejó de
 * existir cuando un territorio sin camión dejó de planificarse con capacidad
 * inventada — sin camión no hay ruta que contar. Lo que queda del viejo KPI es el
 * caso REAL de respaldo: el camión existe pero su capacidad o su jornada no están
 * declaradas en el maestro.
 *
 * La brecha de flota —qué territorios no tienen con qué salir— va por
 * `fleetGapSummary`: son dos afirmaciones distintas y fundirlas en una línea
 * hacía que la más accionable se leyera como un detalle de la otra.
 *
 * Devuelve `''` cuando no hay nada que declarar.
 */
export function groupsSummary(groups: readonly GroupPlanView[]): string {
  const list = groups ?? [];
  const defaults = list.filter((g) => !g.fleetFromMaster).length;
  const failed = list.filter((g) => g.status === 'failed').length;

  const parts: string[] = [];
  if (defaults > 0) {
    parts.push(
      `${defaults} ${defaults === 1 ? 'ruta' : 'rutas'} con capacidad o jornada por defecto`,
    );
  }
  if (failed > 0) {
    parts.push(`${failed} no se pudo planificar`);
  }
  return parts.join(' · ');
}

/**
 * Resumen de la **brecha de flota**, para leerlo sin desplegar el detalle.
 *
 * Es el dato que reemplaza al viejo "N rutas con flota de respaldo", y es de otra
 * naturaleza: no describe cómo se planificó algo, describe lo que **no se puede
 * planificar hoy**. Se arregla asignando un camión en `master/routing/sub-route`,
 * no ajustando la corrida — por eso nombra territorios y no rutas.
 *
 * Devuelve `''` sin brecha: con toda la flota cubierta no hay nada que advertir.
 */
export function fleetGapSummary(gaps: readonly FleetGap[]): string {
  const list = gaps ?? [];
  if (!list.length) return '';
  const territories = list.filter((g) => g.territoryId !== undefined).length;
  const orders = list.reduce((n, g) => n + g.orders, 0);
  const parts: string[] = [];
  if (territories > 0) {
    parts.push(
      `${territories} ${territories === 1 ? 'territorio' : 'territorios'} sin camión asignado`,
    );
  }
  parts.push(`${orders} ${orders === 1 ? 'pedido' : 'pedidos'} sin flota`);
  return parts.join(' · ');
}

/**
 * Aclara el salto de **pedidos** a **visitas** cuando el optimizador agrupa los
 * pedidos de un mismo cliente en una sola parada.
 *
 * Sin esto la pantalla dice "262 pedidos se optimizaron" y más abajo "226/247",
 * y el usuario concluye que se perdieron 15. No se perdió ninguno: son pedidos
 * de clientes que ya tenían otro. La cuenta cierra, pero solo si se dice que la
 * unidad cambió.
 *
 * Devuelve `null` cuando no hay nada que explicar: si coinciden, no hubo
 * agrupación; si hay MENOS pedidos que visitas, el dato es inconsistente y
 * afirmar una fusión sería inventar.
 */
export function groupedOrdersNote(orders: number, stops: number): string | null {
  if (!(orders > stops)) return null;
  return `${orders} pedidos = ${stops} visitas: los pedidos de un mismo cliente se agrupan en una sola visita.`;
}

/**
 * Indicador único de rutas, que sirve para el avance y para el fallo.
 *
 * Mientras la corrida avanza, `planned < total` se lee como progreso; al
 * terminar, se lee como que algo falló. La misma cadena vale para las dos
 * porque en ambas dice la verdad, y evita el chip duplicado de antes (las
 * rutas producidas y los grupos a planificar competían por la palabra "rutas").
 *
 * Con `total = 0` la oficina se planificó sin dividir en grupos: no hay contra
 * qué comparar y se informa solo lo producido.
 */
export function routesProgressLabel(planned: number, total: number): string {
  const noun = planned === 1 ? 'ruta' : 'rutas';
  if (total <= 0) return `${planned} ${noun}`;
  const participle = planned === 1 ? 'planificada' : 'planificadas';
  if (planned === total) return `${planned} ${noun} ${participle}`;
  return `${planned} de ${total} ${noun} ${participle}`;
}

/**
 * KPIs de una oficina desde lo acumulado hasta ahora.
 *
 * En el camino por grupo no hay un único `PlanResponse` del que derivarlos: se
 * recomputan tras cada grupo para que el tablero acompañe el avance.
 */
export function recomputeOfficeDash(view: OfficePlanView): Dash {
  let distanceKm = 0;
  let durationMin = 0;
  let serviceMin = 0;
  let workMin = 0;
  let assigned = 0;
  for (const t of view.truckViews) {
    distanceKm += t.distanceKm;
    durationMin += t.durationMin;
    serviceMin += t.serviceMin;
    workMin += t.workMin;
    assigned += t.stops.length;
  }
  const unassigned = view.unassignedGroups.reduce((n, g) => n + g.items.length, 0);
  return {
    trucks: view.truckViews.length,
    distanceKm,
    durationMin,
    serviceMin,
    workMin,
    assigned,
    total: assigned + unassigned,
  };
}

/** KPIs globales: la suma de los de cada oficina (las que aún no corrieron no suman). */
export function aggregateDash(views: OfficePlanView[]): Dash {
  const agg: Dash = {
    trucks: 0,
    distanceKm: 0,
    durationMin: 0,
    serviceMin: 0,
    workMin: 0,
    assigned: 0,
    total: 0,
  };
  for (const v of views) {
    if (!v.dash) continue;
    agg.trucks += v.dash.trucks;
    agg.serviceMin += v.dash.serviceMin;
    agg.workMin += v.dash.workMin;
    agg.distanceKm += v.dash.distanceKm;
    agg.durationMin += v.dash.durationMin;
    agg.assigned += v.dash.assigned;
    agg.total += v.dash.total;
  }
  return agg;
}

/**
 * Grupos de marcadores del mapa: el ALMACÉN de cada oficina (origen y regreso)
 * más un grupo por camión, con el mismo color que su tarjeta en la lista.
 */
export function buildMarkerGroups(views: OfficePlanView[]): MarkerGroup[] {
  const warehouses: MarkerGroup[] = views
    .map((v) => {
      const geo = officeGeoOf(v.office);
      if (!geo || !v.truckViews.length) return null;
      return {
        groupName: `Almacén · ${v.office.name}`,
        groupColor: WAREHOUSE_COLOR,
        markers: [
          {
            title: `🏭 Almacén — ${v.office.name}`,
            subtitle: `Salida y regreso de los camiones · ${v.office.address?.address ?? ''}`,
            latitude: geo[0],
            longitude: geo[1],
          },
        ],
      };
    })
    .filter((g): g is NonNullable<typeof g> => g !== null);

  return [
    ...warehouses,
    ...views.flatMap((v) =>
      v.truckViews.map((t) => ({
        groupName: `${v.office.name} · ${t.label}`,
        groupColor: t.color,
        markers: t.stops
          .map((s) => {
            if (s.lat == null || s.lon == null) return null;
            return {
              title: `${t.label} — ${s.sequence}. ${s.name}`,
              subtitle: s.address,
              latitude: s.lat,
              longitude: s.lon,
              order: s.sequence,
            };
          })
          .filter((m): m is NonNullable<typeof m> => m !== null),
      })),
    ),
  ];
}


/** "Xh Ym" a partir de minutos. */
export function formatDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

/** Mensaje legible de un fallo del `/plan`, para mostrarlo en la sección. */
export function planErrorMessage(err: unknown): string {
  const detail = apiErrorDetail(err);
  if (detail) return detail;
  const status = (err as { status?: number })?.status;
  if (status === 503) return 'El motor de optimización no está disponible.';
  if (status === 422) return 'Datos insuficientes para optimizar (revisa coordenadas/órdenes).';
  return 'No se pudieron generar las rutas de esta oficina.';
}

/**
 * Motivos distintos de un conjunto de grupos fallidos, con cuántos cayó cada uno.
 *
 * Cuando falla la corrida entera suele ser **un solo** motivo repetido N veces
 * (un campo que el contrato rechaza, el motor caído). Agrupado evita tener que
 * abrir la consola para saber qué pasó.
 */
export function failureReasonsOf(
  groups: ReadonlyArray<{ error?: string }>,
): Array<{ message: string; count: number }> {
  const byMessage = new Map<string, number>();
  for (const g of groups) {
    const message = g.error || 'Error desconocido';
    byMessage.set(message, (byMessage.get(message) ?? 0) + 1);
  }
  return [...byMessage.entries()]
    .map(([message, count]) => ({ message, count }))
    .sort((a, b) => b.count - a.count);
}
